import { z } from "zod";
import { PartnerBadgeSchema } from "./badge";
import { ShopifyPartnerTierSchema, ExpertTypeSchema, ListingTypeSchema } from "./shared";
import { SocialLinksSchema, OfferedServiceSchema, OverallReviewsSchema } from "./expert";
import { CitySchema } from "./cities";

export const ExpertProfileStatusSchema = z.enum([
    "application",
    "draft",
    "published",
    "unpublished",
    "banned",
]);

export const AdminExpertOrganizationSchema = z.object({
    id: z.number(),
    name: z.string(),
    org_type: z.string(),
});

export const ExpertTagSchema = z.object({
    id: z.number(),
    name: z.string(),
});

export const ExpertTagOptionSchema = ExpertTagSchema.extend({
    experts_count: z.number().int().nonnegative(),
});

export const AdminExpertListItemSchema = z.object({
    id: z.number(),
    user_id: z.number(),
    display_name: z.string(),
    email: z.string().nullable(),
    avatar: z.string().nullable(),
    organization: AdminExpertOrganizationSchema.nullable().optional(),
    organization_id: z.number().nullable().optional(),
    expert_type: ExpertTypeSchema,
    listing_type: ListingTypeSchema,
    profile_status: ExpertProfileStatusSchema,
    partner_tier: ShopifyPartnerTierSchema.nullable(),
    country: z.string().nullable(),
    city: CitySchema.nullable().optional().default(null),
    starting_price: z.number().nullable(),
    languages: z.array(z.string()).nullable(),
    offered_services: z.array(OfferedServiceSchema),
    reviews_count: z.number(),
    average_rating: z.number().nullable(),
    badges: z.array(PartnerBadgeSchema),
    is_pro: z.boolean(),
    is_claimed: z.boolean().optional(),
    tags: z.array(ExpertTagSchema).default([]),
    notes_count: z.number().int().nonnegative().default(0),
    created_at: z.string(),
    updated_at: z.string().nullable(),
});

/**
 * Status of the expert's most recent profile claim; null when they have none.
 * Distinct from the list filter's claim_status (claimed/unclaimed ownership).
 */
export const ExpertLatestClaimStatusSchema = z.enum([
    "pending",
    "verified",
    "approved",
    "rejected",
]);

/** The login account the `user` group of the details endpoint edits. */
export const AdminExpertUserSchema = z.object({
    id: z.number(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    email: z.string(),
});

export const AdminExpertFreelancerProfileSchema = z.object({
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    role: z.string().nullable(),
});

export const AdminExpertAgencyProfileSchema = z.object({
    agency_name: z.string().nullable(),
    representative_name: z.string().nullable(),
    representative_role: z.string().nullable(),
});

/**
 * Unlike the list item's `email` (primary user only), the detail endpoint's
 * `email` falls back to the listing contact email for unclaimed/imported
 * profiles that have no login user.
 */
export const AdminExpertDetailSchema = AdminExpertListItemSchema.extend({
    // Older backends omit flags; missing means unknown, not an empty replacement list.
    flags: z.array(z.string()).optional(),
    latest_claim_status: ExpertLatestClaimStatusSchema.nullable().optional(),
    slug: z.string().nullable(),
    bio: z.string().nullable(),
    timezone: z.string().nullable(),
    phone_no: z.string().nullable(),
    regions_served: z.array(z.string()).nullable(),
    geo_regions: z.array(z.string()).default([]),
    social_links: SocialLinksSchema.nullable(),
    reviews: OverallReviewsSchema.nullable(),
    // Editable groups the details endpoint writes. Optional until the API exposes
    // them on the detail resource; the editor hides fields it has no value for.
    user: AdminExpertUserSchema.nullable().optional(),
    freelancer: AdminExpertFreelancerProfileSchema.nullable().optional(),
    agency: AdminExpertAgencyProfileSchema.nullable().optional(),
    subcategory_limit_override: z.number().int().nullable().optional(),
    subcategory_limit: z.number().int().optional(),
    subcategory_plan_limit: z.number().int().optional(),
});

const NullableString = z.string().nullable();

/**
 * Sparse by design: only the keys present are written, so callers send the
 * fields that changed. `null` clears a nullable field.
 */
export const UpdateExpertDetailsRequestSchema = z.object({
    user: z.object({
        first_name: NullableString,
        last_name: NullableString,
        email: z.string(),
    }).partial().optional(),
    expert: z.object({
        bio: NullableString,
        country: NullableString,
        city_id: z.number().int().nullable(),
        starting_price: z.number().nullable(),
        timezone: NullableString,
        phone_no: NullableString,
        languages: z.array(z.string()),
        geo_regions: z.array(z.string()),
    }).partial().optional(),
    freelancer: z.object({
        first_name: z.string(),
        last_name: z.string(),
        role: NullableString,
    }).partial().optional(),
    agency: z.object({
        agency_name: z.string(),
        representative_name: NullableString,
        representative_role: NullableString,
    }).partial().optional(),
    social_links: z.object({
        contact_email: NullableString,
        linkedin_url: NullableString,
        github_url: NullableString,
        x_url: NullableString,
        fiverr_url: NullableString,
        upwork_url: NullableString,
        toptal_url: NullableString,
        storetasker_url: NullableString,
        other_listing_url: NullableString,
        website: NullableString,
        shopify_directory_link: NullableString,
        booking_call_url: NullableString,
        google_merchant: NullableString,
    }).partial().optional(),
    offered_services: z.object({
        subcategory_limit_override: z.number().int().nullable(),
        subcategory_ids: z.array(z.number().int()),
    }).partial().optional(),
});

export const ExpertClaimFilterStatusSchema = z.enum(["claimed", "unclaimed"]);

export const AdminExpertFilterOptionsSchema = z.object({
    statuses: z.array(z.string()),
    expert_types: z.array(z.string()),
    countries: z.array(z.string()),
    service_categories: z.array(z.string()),
    languages: z.array(z.string()),
    partner_tiers: z.array(z.string()),
    claim_statuses: z.array(ExpertClaimFilterStatusSchema).default([]),
});

/**
 * The exact snapshot a filter preset may store. The API rejects unknown keys
 * (as well as `cursor` and `limit`), so this must stay in lockstep with
 * ExpertManagerFilterRules on the backend.
 */
export const ExpertFilterSnapshotSchema = z.object({
    search: z.string().optional(),
    status: ExpertProfileStatusSchema.optional(),
    expert_type: z.string().optional(),
    country: z.string().optional(),
    service_category: z.string().optional(),
    partner_tier: z.string().optional(),
    language: z.string().optional(),
    is_pro: z.boolean().optional(),
    claim_status: ExpertClaimFilterStatusSchema.optional(),
    tag_ids: z.array(z.number().int().positive()).optional(),
    exclude_tag_ids: z.array(z.number().int().positive()).optional(),
});

export const ExpertFilterPresetSchema = z.object({
    id: z.number(),
    name: z.string(),
    filters: ExpertFilterSnapshotSchema,
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});

export const ExpertFilterPresetRequestSchema = z.object({
    name: z.string().min(1).max(100),
    filters: ExpertFilterSnapshotSchema,
});

export const SyncExpertTagsRequestSchema = z.object({
    tags: z.array(z.string()).max(25),
});

export const SyncExpertTagsResponseSchema = z.object({
    expert_id: z.number(),
    tags: z.array(ExpertTagSchema),
});

export const AdminSummarySchema = z.object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    avatar: z.string().nullable(),
});

export const ExpertNoteSchema = z.object({
    id: z.number(),
    expert_id: z.number(),
    body: z.string(),
    author: AdminSummarySchema,
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});

export const CreateExpertNoteRequestSchema = z.object({
    body: z.string().min(1).max(5000),
});

export const ExpertNoteListMetaSchema = z.object({
    total: z.number().int().nonnegative(),
});

export const AdminExpertListMetaSchema = z.object({
    total: z.number().int().nonnegative(),
});

export const UpdateExpertStatusRequestSchema = z.object({
    action: z.enum(["publish", "unpublish", "set_draft", "set_application"]),
});

export const UpdateExpertStatusResponseSchema = z.object({
    id: z.number(),
    profile_status: ExpertProfileStatusSchema,
});

export const UpdateExpertSlugRequestSchema = z.object({
    slug: z.string().min(1),
    create_redirect: z.boolean().optional(),
});

export const ExpertSlugRedirectSchema = z.object({
    id: z.number(),
    source_path: z.string(),
    target_path: z.string(),
    status_code: z.number(),
});

export const UpdateExpertSlugResponseSchema = z.object({
    id: z.number(),
    slug: z.string(),
    redirect: ExpertSlugRedirectSchema.nullable(),
});

export type ExpertProfileStatus = z.infer<typeof ExpertProfileStatusSchema>;
export type AdminExpertOrganization = z.infer<typeof AdminExpertOrganizationSchema>;
export type AdminExpertListItem = z.infer<typeof AdminExpertListItemSchema>;
export type AdminExpertDetail = z.infer<typeof AdminExpertDetailSchema>;
export type AdminExpertUser = z.infer<typeof AdminExpertUserSchema>;
export type AdminExpertFreelancerProfile = z.infer<typeof AdminExpertFreelancerProfileSchema>;
export type AdminExpertAgencyProfile = z.infer<typeof AdminExpertAgencyProfileSchema>;
export type UpdateExpertDetailsRequest = z.infer<typeof UpdateExpertDetailsRequestSchema>;
export type ExpertDetailsGroup = keyof UpdateExpertDetailsRequest;
export type AdminExpertFilterOptions = z.infer<typeof AdminExpertFilterOptionsSchema>;
export type AdminExpertListMeta = z.infer<typeof AdminExpertListMetaSchema>;
/** Named apart from ExpertClaimStatus in expert-claims, which is a claim request's own state. */
export type ExpertClaimFilterStatus = z.infer<typeof ExpertClaimFilterStatusSchema>;
export type ExpertLatestClaimStatus = z.infer<typeof ExpertLatestClaimStatusSchema>;
export type ExpertTag = z.infer<typeof ExpertTagSchema>;
export type ExpertTagOption = z.infer<typeof ExpertTagOptionSchema>;
export type ExpertFilterSnapshot = z.infer<typeof ExpertFilterSnapshotSchema>;
export type ExpertFilterPreset = z.infer<typeof ExpertFilterPresetSchema>;
export type ExpertFilterPresetRequest = z.infer<typeof ExpertFilterPresetRequestSchema>;
export type SyncExpertTagsRequest = z.infer<typeof SyncExpertTagsRequestSchema>;
export type SyncExpertTagsResponse = z.infer<typeof SyncExpertTagsResponseSchema>;
export type AdminSummary = z.infer<typeof AdminSummarySchema>;
export type ExpertNote = z.infer<typeof ExpertNoteSchema>;
export type CreateExpertNoteRequest = z.infer<typeof CreateExpertNoteRequestSchema>;
export type ExpertNoteListMeta = z.infer<typeof ExpertNoteListMetaSchema>;
const StatusCountsSchema = z.object({
    application: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    published: z.number().int().nonnegative(),
    unpublished: z.number().int().nonnegative(),
    banned: z.number().int().nonnegative(),
});

const TypeBucketSchema = z.object({
    total: z.number().int().nonnegative(),
    statuses: StatusCountsSchema,
});

export const AdminExpertManagerStatsSchema = z.object({
    all_experts: TypeBucketSchema,
    types: z.object({
        freelancer: TypeBucketSchema,
        agency: TypeBucketSchema,
        app_service_provider: TypeBucketSchema,
    }),
});

export type StatusCounts = z.infer<typeof StatusCountsSchema>;
export type ExpertTypeBucket = z.infer<typeof TypeBucketSchema>;
export type AdminExpertManagerStats = z.infer<typeof AdminExpertManagerStatsSchema>;
export type UpdateExpertStatusRequest = z.infer<typeof UpdateExpertStatusRequestSchema>;
export type UpdateExpertStatusResponse = z.infer<typeof UpdateExpertStatusResponseSchema>;
export type UpdateExpertSlugRequest = z.infer<typeof UpdateExpertSlugRequestSchema>;
export type UpdateExpertSlugResponse = z.infer<typeof UpdateExpertSlugResponseSchema>;
export type ExpertSlugRedirect = z.infer<typeof ExpertSlugRedirectSchema>;

export const ExpertConversionPeriodStatsSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    note: z.string().nullable().optional(),
    matched_leads: z.number().int().nonnegative(),
    converted_leads: z.number().nonnegative(),
    rejected_leads: z.number().int().nonnegative(),
    conversion_rate: z.number().min(0).max(100),
    rejection_rate: z.number().min(0).max(100),
});

export const AdminExpertConversionStatsSchema = z.object({
    expert_id: z.number(),
    note: z.string().nullable().optional(),
    periods: z.array(ExpertConversionPeriodStatsSchema),
});

export type ExpertConversionPeriodStats = z.infer<typeof ExpertConversionPeriodStatsSchema>;
export type AdminExpertConversionStats = z.infer<typeof AdminExpertConversionStatsSchema>;

export const SwitchExpertTypeRequestSchema = z.object({
    expert_type: z.enum(['freelancer', 'agency', 'app_service_provider']),
    agency_name: z.string().optional(),
    representative_name: z.string().optional(),
    representative_role: z.string().optional(),
    representative_linkedin: z.string().optional(),
    agency_type_fields: z.object({
        app_tier: z.literal('built_for_shopify').optional(),
    }).optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    role: z.string().optional(),
});

export const SwitchExpertTypeResponseSchema = z.object({
    expert_id: z.number(),
    previous_type: ExpertTypeSchema,
    previous_listing_type: ListingTypeSchema,
    current_type: ExpertTypeSchema,
    current_listing_type: ListingTypeSchema,
    agency_type: z.string().nullable().optional(),
    organization_id: z.number(),
    organization_type: z.string(),
});

export type SwitchExpertTypeRequest = z.infer<typeof SwitchExpertTypeRequestSchema>;
export type SwitchExpertTypeResponse = z.infer<typeof SwitchExpertTypeResponseSchema>;
